import React, { useId, useState } from 'react';
import { Info, Plus, X } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  DatePicker,
  Label,
  RadioButtonGroup,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import type { GoalTemplateDetail } from '../../services/api/types.gen';
import type { DisciplineType } from '../../services/api/models/DisciplineType';
import { addGoalToVisit, addKinnserGoalToEpisode } from '../../services/api';
import { useStore } from '../../store';
import { useToast } from '../../hooks/useToastContext';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import {
  formatDateToISO,
  getBusinessTodayDate,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import {
  GOAL_DISCIPLINE_OPTIONS,
  GOAL_TYPE_OPTIONS,
  renderEditableInstructionFields,
} from '../../utils/goals';
import CreateInterventionFromTemplateCard from './CreateInterventionFromTemplateCard';

interface CreateGoalFromTemplateCardProps {
  template: GoalTemplateDetail | null;
  episodeStartDate: string;
  episodeEndDate: string;
  /** Save the goal onto this visit (the normal flow). */
  visitId?: string;
  /**
   * Kinnser-migration flow: save the goal directly onto this episode, with no
   * visit. Free-text interventions can be added on the card, since a goal not
   * tied to a visit has no later Add Interventions path. Provide exactly one
   * of visitId / episodeId.
   */
  episodeId?: string;
  /**
   * The discipline this goal is being added under (EMR-1661), captured when
   * the goal was picked -- an order's dropdown value, or the discipline a
   * Kinnser template option was listed under. Sent as-is (template and free
   * text alike); the server 409s a template that does not serve it. Leave
   * unset for a visit, whose own discipline is authoritative. Off a visit
   * with none supplied (a Kinnser free-text goal, or a template option under
   * several disciplines) the card asks for one and requires it to save.
   */
  discipline?: DisciplineType;
  /**
   * The disciplines the template serves, for the card's own discipline
   * select when none was picked: only these can save, so only these are
   * offered. Unset (a free-text goal) offers every goal discipline.
   */
  servedDisciplines?: ReadonlyArray<DisciplineType>;
  onSave?: () => void;
  onCancel?: () => void;
}

const CreateGoalFromTemplateCard: React.FC<CreateGoalFromTemplateCardProps> = ({
  template,
  episodeStartDate,
  episodeEndDate,
  visitId,
  episodeId,
  discipline: pickedDiscipline,
  servedDisciplines,
  onSave,
  onCancel,
}) => {
  const [targetDate, setTargetDate] = useState<Date | undefined>(undefined);
  const [backdatedCreationDate, setBackdatedCreationDate] = useState<
    Date | undefined
  >(undefined);
  const [isEndOfEpisode, setIsEndOfEpisode] = useState(false);
  const [goalType, setGoalType] = useState<'short_term' | 'long_term'>(
    'short_term'
  );
  const [instructionValues, setInstructionValues] = useState<
    Record<string, string>
  >({});
  const [freeTextInstruction, setFreeTextInstruction] = useState('');
  const [discipline, setDiscipline] = useState<DisciplineType | undefined>(
    undefined
  );
  const [interventionData, setInterventionData] = useState<
    Record<string, { instruction: string; isValid: boolean }>
  >({});
  const [removedInterventionIds, setRemovedInterventionIds] = useState<
    Set<string>
  >(new Set());
  const [freeTextInterventionKeys, setFreeTextInterventionKeys] = useState<
    string[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const uid = useId();
  const { success: showSuccess, error: showError } = useToast();
  const agency = useStore(state => state.user?.agency);
  // Backdating exists for agencies migrating goals from Kinnser into Roger,
  // so it only shows when the agency runs both EMRs.
  const canBackdateCreation = !!agency?.is_kinnser_enabled;

  const minDate = parseISOToLocalDate(episodeStartDate);
  const maxDate = parseISOToLocalDate(episodeEndDate);

  const isFreeText = template === null;
  const isEpisodeMode = !!episodeId;
  // Off a visit the server needs a discipline; the caller normally supplies
  // the one the goal was picked under, and only the episode-scoped Kinnser
  // flow can leave it open (EMR-1661).
  const needsDiscipline = isEpisodeMode && pickedDiscipline === undefined;
  const effectiveDiscipline = pickedDiscipline ?? discipline;
  const editableKeys = isFreeText
    ? []
    : Object.entries(template.instruction)
        .filter(([, field]) => field.editable)
        .map(([key]) => key);

  const visibleAttachedInterventions = isFreeText
    ? []
    : template.attached_interventions.filter(
        iv => !removedInterventionIds.has(iv.id)
      );
  // The discipline gate applies to template and free-text goals alike: a
  // template option under several disciplines also asks the card.
  const hasRequiredDiscipline = !needsDiscipline || !!discipline;
  const isFormValid =
    hasRequiredDiscipline &&
    (isFreeText
      ? !!targetDate &&
        !!freeTextInstruction.trim() &&
        freeTextInterventionKeys.every(key => interventionData[key]?.isValid)
      : !!targetDate &&
        editableKeys.every(key => instructionValues[key]?.trim()) &&
        visibleAttachedInterventions.every(
          iv => interventionData[iv.id]?.isValid
        ));
  const disciplineOptions = servedDisciplines
    ? GOAL_DISCIPLINE_OPTIONS.filter(option =>
        servedDisciplines.includes(option.value)
      )
    : GOAL_DISCIPLINE_OPTIONS;

  const assembleInstruction = () => {
    if (isFreeText) {
      return freeTextInstruction.trim();
    }
    const entries = Object.entries(template.instruction).sort(
      ([a], [b]) => Number(a) - Number(b)
    );
    return entries
      .map(([key, field]) =>
        field.editable ? (instructionValues[key] ?? '') : field.text
      )
      .join(' ');
  };

  const handleDateChange = (date: Date | undefined) => {
    setIsEndOfEpisode(false);
    if (!date) {
      setTargetDate(undefined);
      return;
    }
    const time = date.getTime();
    if (minDate && time < minDate.getTime()) {
      setTargetDate(minDate);
    } else if (maxDate && time > maxDate.getTime()) {
      setTargetDate(maxDate);
    } else {
      setTargetDate(date);
    }
  };

  const handleInstructionChange = (key: string, value: string) => {
    setInstructionValues(prev => ({ ...prev, [key]: value }));
  };

  const handleFreeTextInstructionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setFreeTextInstruction(e.target.value);
  };

  const handleEndOfEpisodeChange = (checked: boolean | 'indeterminate') => {
    const checkedVal = checked === true;
    setIsEndOfEpisode(checkedVal);
    if (checkedVal) {
      const end = parseISOToLocalDate(episodeEndDate);
      if (end) setTargetDate(end);
    }
  };

  const handleGoalTypeChange = (val: string) => {
    setGoalType(val as 'short_term' | 'long_term');
  };

  const handleInterventionChange =
    (ivId: string) => (data: { instruction: string; isValid: boolean }) => {
      setInterventionData(prev => ({ ...prev, [ivId]: data }));
    };

  const handleRemoveIntervention = (ivId: string) => () => {
    setRemovedInterventionIds(prev => new Set([...prev, ivId]));
  };

  const handleAddFreeTextIntervention = () => {
    setFreeTextInterventionKeys(prev => [
      ...prev,
      `free-text-intervention-${Date.now()}`,
    ]);
  };

  const handleRemoveFreeTextIntervention = (key: string) => () => {
    setFreeTextInterventionKeys(prev => prev.filter(k => k !== key));
    setInterventionData(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSave = async () => {
    if (!targetDate) return;
    setIsSaving(true);
    try {
      const body = {
        goal_template_id: isFreeText ? null : template.id,
        target_date: formatDateToISO(targetDate),
        is_for_end_of_episode: isEndOfEpisode,
        length: goalType,
        name: isFreeText ? 'Free Text' : template.name,
        instruction: assembleInstruction(),
        backdated_creation_date:
          canBackdateCreation && backdatedCreationDate
            ? formatDateToISO(backdatedCreationDate)
            : null,
        interventions: isFreeText
          ? freeTextInterventionKeys.map(key => ({
              intervention_id: null,
              name: 'Free Text',
              instruction: interventionData[key]?.instruction ?? '',
            }))
          : visibleAttachedInterventions.map(iv => ({
              intervention_id: iv.id,
              name: iv.name,
              instruction: interventionData[iv.id]?.instruction ?? '',
            })),
        ...(effectiveDiscipline ? { discipline: effectiveDiscipline } : {}),
      };
      if (episodeId) {
        await addKinnserGoalToEpisode({
          path: { episode_id: episodeId },
          body,
          throwOnError: true,
        });
      } else if (visitId) {
        await addGoalToVisit({
          path: { visit_id: visitId },
          body,
          throwOnError: true,
        });
      } else {
        return;
      }
      showSuccess('Goal added', 'The goal has been saved.');
      onSave?.();
    } catch (err) {
      console.error('Failed to add goal', err);
      // A 409 names the discipline the template is for (EMR-1661).
      showError(
        'Error adding goal',
        getBusinessErrorMessage(err) ?? 'There was an error adding the goal.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className='self-start p-2.5 sm:p-2.5 md:p-2.5'>
      <CardHeader className='mb-2 relative pr-8 p-0'>
        <CardTitle className='text-sm font-semibold leading-tight'>
          <span className='text-muted-foreground font-normal'>New Goal: </span>
          {isFreeText ? 'Free Text' : template.name}
        </CardTitle>
        {onCancel && (
          <button
            type='button'
            onClick={onCancel}
            className='absolute top-0 right-0 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors'
            aria-label='Discard and remove'
          >
            <X className='h-4 w-4' />
          </button>
        )}
      </CardHeader>

      <CardContent className='space-y-3 p-0'>
        <div className='space-y-2'>
          <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
            Target date for meeting a goal
          </p>
          <div className='flex flex-wrap items-center gap-2'>
            <DatePicker
              date={targetDate}
              onDateChange={handleDateChange}
              datePickerSize='sm'
              typable
              showClearButton
              minDate={minDate}
              maxDate={maxDate}
              className='w-44'
            />
            <div className='flex items-center gap-2'>
              <Checkbox
                id={`end-of-episode-${uid}`}
                checked={isEndOfEpisode}
                onCheckedChange={handleEndOfEpisodeChange}
                checkboxSize='sm'
              />
              <Label
                htmlFor={`end-of-episode-${uid}`}
                className='text-sm cursor-pointer select-none'
              >
                For end of episode
              </Label>
            </div>
            <div className='flex shrink-0'>
              <RadioButtonGroup
                value={goalType}
                onValueChange={handleGoalTypeChange}
                options={GOAL_TYPE_OPTIONS}
                orientation='horizontal'
              />
            </div>
          </div>
        </div>

        {needsDiscipline && (
          <>
            <Separator />
            <div className='space-y-2'>
              <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Discipline
              </p>
              <Label htmlFor={`discipline-${uid}`} className='sr-only'>
                Discipline
              </Label>
              <Select
                value={discipline}
                onValueChange={val => setDiscipline(val as DisciplineType)}
              >
                <SelectTrigger
                  id={`discipline-${uid}`}
                  selectSize='sm'
                  className='w-44'
                >
                  <SelectValue placeholder='Select discipline' />
                </SelectTrigger>
                <SelectContent>
                  {disciplineOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {canBackdateCreation && (
          <>
            <Separator />
            <div className='space-y-2'>
              <div className='flex items-center gap-1.5'>
                <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  Original creation date (optional)
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className='h-3.5 w-3.5 text-muted-foreground shrink-0'
                        aria-label='About the original creation date'
                      />
                    </TooltipTrigger>
                    <TooltipContent className='max-w-64'>
                      Moving this goal from Kinnser? Enter the date it was first
                      created there. Leave blank for new goals. Add all
                      interventions before you save this goal. You cannot add
                      them later.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <DatePicker
                date={backdatedCreationDate}
                onDateChange={setBackdatedCreationDate}
                datePickerSize='sm'
                typable
                showClearButton
                // Pacific business day, matching the API's future-date check —
                // a browser-local today can be a day ahead east of Pacific.
                maxDate={getBusinessTodayDate()}
                className='w-44'
              />
            </div>
          </>
        )}

        <Separator />

        <div className='space-y-2'>
          <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
            Instruction
          </p>
          <div className='leading-relaxed'>
            {isFreeText ? (
              <Textarea
                placeholder='Enter goal instruction...'
                value={freeTextInstruction}
                onChange={handleFreeTextInstructionChange}
                textareaSize='default'
                className='min-h-[64px]'
              />
            ) : (
              renderEditableInstructionFields(
                template.instruction,
                instructionValues,
                handleInstructionChange
              )
            )}
          </div>
        </div>

        {!isFreeText && template.attached_interventions.length > 0 && (
          <>
            <Separator />
            <div className='space-y-3'>
              <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Interventions
              </p>
              {visibleAttachedInterventions.map(iv => (
                <CreateInterventionFromTemplateCard
                  key={iv.id}
                  template={iv}
                  onChange={handleInterventionChange(iv.id)}
                  onCancel={handleRemoveIntervention(iv.id)}
                />
              ))}
            </div>
          </>
        )}

        {/* A goal saved to the episode has no visit, so interventions cannot be
            added later through Add Interventions — offer them here instead. */}
        {isEpisodeMode && isFreeText && (
          <>
            <Separator />
            <div className='space-y-3'>
              <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Interventions
              </p>
              {freeTextInterventionKeys.map(key => (
                <CreateInterventionFromTemplateCard
                  key={key}
                  template={null}
                  onChange={handleInterventionChange(key)}
                  onCancel={handleRemoveFreeTextIntervention(key)}
                />
              ))}
              <Button
                size='sm'
                variant='outline'
                onClick={handleAddFreeTextIntervention}
                className='flex items-center gap-1'
              >
                <Plus className='h-4 w-4' />
                Add Intervention
              </Button>
            </div>
          </>
        )}

        <div className='flex justify-end pt-1'>
          <Button
            size='sm'
            disabled={!isFormValid || isSaving}
            onClick={handleSave}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CreateGoalFromTemplateCard;
